/**
 * Confirms a detail-view asset status change and backstops a missing
 * smart-crop rejection label before the update can be submitted.
 */

import React from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { type AssetDto } from 'src/store/generated/leela-api-generated';
import { approvalIcon, SingleAssetList } from './AssetList';
import { capitalizeFirstLetter } from 'src/utils';
import type { RejectReasonSelection } from '../RejectReasonSelect';
import {
  getIncompleteRejectAssets,
  RejectReasonBackstop,
} from './RejectReasonBackstop';

export interface AssetDetailApprovalModalModalProps {
  action: string;
  asset: AssetDto | undefined;
  incompleteRejectAssets?: readonly AssetDto[];
  isLoading?: boolean;
  open: boolean;
  rejectReasonValues?: ReadonlyMap<string, RejectReasonSelection>;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
  onRejectReasonChange?: (asset: AssetDto, value: RejectReasonSelection) => void;
  // productStrategyId → display name; the confirm preview shows which single strategy row is written.
  strategyLabels?: Map<string, string>;
}

export const AssetDetailApprovalModal = (
  props: AssetDetailApprovalModalModalProps,
) => {
  const {
    action,
    asset,
    incompleteRejectAssets = [],
    isLoading = false,
    open,
    rejectReasonValues,
    strategyLabels,
    onCancel,
    onConfirm,
    onRejectReasonChange,
  } = props;
  const { t } = useTranslation();
  const hasIncompleteRejectReasons = getIncompleteRejectAssets(
    incompleteRejectAssets,
    rejectReasonValues,
  ).length > 0;

  return (
    <Modal
      data-testid="asset-detail-action-confirmation-modal"
      open={open}
      scrollContainer="viewport"
      title={t('header_asset_status_update_modal')}
      width="max(800px, 90vw)"
    >
      {asset && (
        <Column alignmentHorizontal="start" width="100%">
          <Row>
              {approvalIcon(action)}
              <Text type="b300">
                {t('modal_single_asset_approval_confirm_header', {
                  status: capitalizeFirstLetter(action),
                })}
              </Text>
            </Row>
          <SingleAssetList assets={[asset]} strategyLabels={strategyLabels} />
          <RejectReasonBackstop
            assets={incompleteRejectAssets}
            rejectReasonValues={rejectReasonValues}
            onRejectReasonChange={onRejectReasonChange}
          />
        </Column>
      )}
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          {isLoading && (
            <Row
              data-testid="assset-action-loader"
              spacingInset="none 300"
              wrap="none"
            >
              <Loader size="medium" />
              <Text>{t('loader_loading')}</Text>
            </Row>
          )}

          <Button
            data-testid="cancel-asset-action"
            disabled={isLoading}
            size="small"
            type="secondary"
            onClick={onCancel}
          >
            {t('buttons_cancel')}
          </Button>
          <Button
            data-testid="confirm-asset-action"
            disabled={isLoading || hasIncompleteRejectReasons}
            size="small"
            type="primary"
            onClick={onConfirm}
          >
            {t('buttons_confirm')}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
